/**
 * 1.8.4 §3 (D-1.8.4-3) — Today: the whole house at a glance. For the administrator and a co-administrator; the viewer
 * sees titles and counts only (the API sends no name the role does not already see). In this order, headings and
 * lines, no paragraphs of explanation:
 *
 *   1. the greeting — "Good morning, Eli." (the person's "Call me" from My settings, else their first name);
 *   2. Needs your attention — every program, one line per kind, one button to where it is fixed;
 *   3. the day — every line of the agency's day with its program word and its time, "now" on today; List (by time) or
 *      Grid (a column per program), remembered on the device; beside it on a wide screen the month calendar (a week
 *      strip above it on a phone) to pick any day — the day is in the address (`?date=yyyyMMdd`; 26 Oct 2026: `?day=` is the Housekeeping Day's, at `/housekeeping`); a past day reads as
 *      a record;
 *   4. the emails armed ahead of time for the day (event reminders) — Open goes to its Outbox row, where Cancel lives;
 *   5. the recent reports — one line each, open to read.
 *
 * Tap a line → a small window: what, when, who, Call (no number printed, D-1.8.3-1) and Open in [program]. Today
 * changes nothing: it is the glance. It refreshes on return to the app.
 */
import { useEffect, useMemo, useRef, useState, type MouseEvent, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { addDaysToKey, isDateKey, serviceMinutesNow } from '@shared/dates';
import { calMonthOf, type CalLine } from '@shared/house-calendar';
import { HOUSE_DAY_PROGRAMS, greetingName, type HouseProgram } from '@shared/house-today';
import { clockWithPeriod } from '@shared/dayplan';
import type { Me } from '@/auth/useMe';
import { useMeExtras } from '@/lib/admin';
import { formatDayKey, formatDayShort, greetingKey, serviceDateKey } from '@/lib/dates';
import { useAnnounce } from '@/components/Announcer';
import { Button } from '@/components/ui/button';
import { langOf } from '@/features/calendar/lineWords';
import { useOpenIn, type Program } from '@/app/program';
import { useHouseCalendar, useHouseToday } from './api';
import { useView } from './useView';
import { LineWindow } from './LineWindow';
import { categoryWord, dayItems, emailWords, itemTime, programWord, reportWords, type DayItem } from './words';
import { AttentionGroups } from './AttentionGroups';
import { MightSlip } from './MightSlip';
import { DoNext } from './DoNext';
import { doNextOn } from '@shared/do-next';
import { useDayPlan } from '@/lib/queries';
import { ThisWeek } from './ThisWeek';
import '@/styles/today184.css';
import '@/styles/today187.css';
import '@/styles/today1025.css';

/** The minute, ticking — "now" moves along the day. */
function useNow(ms = 30_000): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), ms);
    return () => window.clearInterval(id);
  }, [ms]);
  return now;
}

/** A link that also moves the program strip (useOpenIn) — still a real link for a new tab or a screen reader. */
function OpenLink({ href, program, children, ...rest }: { href: string; program: Program | null; children: ReactNode; className?: string; 'data-testid'?: string; 'aria-label'?: string }) {
  const openIn = useOpenIn();
  const onClick = (e: MouseEvent<HTMLAnchorElement>) => {
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    openIn(href, program);
  };
  return (
    <Link to={href} onClick={onClick} {...rest}>
      {children}
    </Link>
  );
}

function LineButton({ item, onOpen }: { item: DayItem; onOpen: (i: DayItem, el: HTMLElement) => void }) {
  const { t, i18n } = useTranslation();
  const lang = langOf(i18n.language);
  return (
    <button type="button" className="t184-line" data-cat={item.category} aria-haspopup="dialog" onClick={(e) => onOpen(item, e.currentTarget)} data-testid={`t184-line-${item.id}`}>
      <span className="t184-time">{itemTime(item, t, lang)}</span>
      <span className="cal182-tag" data-cat={item.category}>
        <span className="cal182-dot" aria-hidden="true" />
        {categoryWord(item.category, t)}
      </span>
      {item.what ? <span className="t184-what">{item.what}</span> : null}
    </button>
  );
}

/** Any day, without the month grid: the device's own date picker (the month is on the Calendar page). */
function DayPicker({ day, onPick }: { day: string; onPick: (k: string) => void }) {
  const { t } = useTranslation();
  return (
    <label className="t184-dayinput t1025-pick">
      <span>{t('today184.pick')}</span>
      <input
        type="date"
        className="input"
        value={`${day.slice(0, 4)}-${day.slice(4, 6)}-${day.slice(6, 8)}`}
        onChange={(e) => {
          const k = e.currentTarget.value.replace(/-/g, '');
          if (isDateKey(k)) onPick(k);
        }}
        data-testid="t184-day-input"
      />
    </label>
  );
}

export function HouseTodayPage({ me }: { me: Me }) {
  const { t, i18n } = useTranslation();
  const lang = langOf(i18n.language);
  const announce = useAnnounce();
  const [params, setParams] = useSearchParams();
  const realToday = serviceDateKey();
  const asked = params.get('date');
  const day = asked && isDateKey(asked) ? asked : realToday;
  const month = calMonthOf(day);
  const q = useHouseToday(day);
  const cal = useHouseCalendar(month);
  const extras = useMeExtras();
  const [view, setView] = useView();
  const now = useNow();
  const todayKey = q.data?.todayKey ?? cal.data?.todayKey ?? realToday;
  const isToday = day === todayKey;
  const isPast = day < todayKey;
  // This week: the next seven days — the month of today and the month a week on (one read when they are the same month)
  const calNow = useHouseCalendar(calMonthOf(todayKey));
  const calWeek = useHouseCalendar(calMonthOf(addDaysToKey(todayKey, 7)));
  const weekLines = useMemo<CalLine[]>(() => [...(calNow.data?.lines ?? []), ...(calWeek.data && calWeek.data.month !== calNow.data?.month ? calWeek.data.lines : [])], [calNow.data, calWeek.data]);

  // refresh on return to the app, as Housekeeping's Today does — each distinct picture once (1103 D-1103-SPEED-1):
  // the three calendar reads often share one month, and the app's own focus refresh joins a read already on its way
  const queryClient = useQueryClient();
  const dayKeyForRefresh = day;
  const monthKeyForRefresh = month;
  const nowMonthForRefresh = calMonthOf(todayKey);
  const weekMonthForRefresh = calMonthOf(addDaysToKey(todayKey, 7));
  useEffect(() => {
    const seen = () => {
      if (document.visibilityState !== 'visible') return;
      const keys = new Map<string, readonly unknown[]>();
      for (const k of [['house-today', dayKeyForRefresh], ['house-calendar', monthKeyForRefresh], ['house-calendar', nowMonthForRefresh], ['house-calendar', weekMonthForRefresh]] as const) keys.set(JSON.stringify(k), k);
      for (const k of keys.values()) void queryClient.refetchQueries({ queryKey: k, exact: true, type: 'active' }, { cancelRefetch: false });
    };
    document.addEventListener('visibilitychange', seen);
    window.addEventListener('pageshow', seen);
    return () => {
      document.removeEventListener('visibilitychange', seen);
      window.removeEventListener('pageshow', seen);
    };
  }, [queryClient, dayKeyForRefresh, monthKeyForRefresh, nowMonthForRefresh, weekMonthForRefresh]);

  const pick = (k: string) => {
    if (k === day) return;
    const p = new URLSearchParams(params);
    if (k === todayKey) p.delete('date');
    else p.set('date', k);
    setParams(p);
    announce(t('today184.showing', { day: formatDayKey(k, i18n.language) }));
  };

  // the day's month, as the house calendar reads it
  const monthLines = useMemo<CalLine[]>(() => (cal.data?.month === month ? cal.data.lines : []), [cal.data, month]);

  // the day's lines: the calendar's, with each housekeeping visit for the office roles
  const visits = q.data && q.data.dayKey === day && q.data.names === 'full' ? q.data.visits : null;
  const items = useMemo(() => dayItems(monthLines, day, visits, t, lang), [monthLines, day, visits, t, lang]);
  const nowMinutes = serviceMinutesNow(now);
  const nowAt = isToday ? (() => {
    const i = items.findIndex((x) => x.minutes !== null && x.minutes > nowMinutes);
    return i < 0 ? items.length : i;
  })() : -1;

  const [opened, setOpened] = useState<DayItem | null>(null);
  const opener = useRef<HTMLElement | null>(null);
  const open = (i: DayItem, el: HTMLElement) => {
    opener.current = el;
    setOpened(i);
  };

  const dayPlan = useDayPlan();
  const doNext = doNextOn(dayPlan.data?.settings);
  // review 3 Nov: wait for the switch's own read (fast) so Today never flips from Do next to the grouped view
  const doNextKnown = !dayPlan.isPending;
  const name = greetingName(extras.data?.settings?.callMe ?? null, me.name);
  const hello = t(greetingKey(now));
  const data = q.data?.dayKey === day ? q.data : null;
  const emails = data?.emails ?? [];
  const reports = q.data?.reports ?? [];
  const unavailable = [...(q.data?.unavailable ?? []).map((s) => t(`today184.section.${s}`)), ...(cal.data?.month === month ? (cal.data.unavailable ?? []).map((c) => t(`cal182.cat.${c}`)) : [])];
  const dayWords = formatDayKey(day, i18n.language);
  const attentionTotal = (q.data?.attention ?? []).reduce((n, l) => n + l.count, 0);
  const todayVisits = (q.data?.visits ?? []).filter((v) => v.status !== 'Cancelled' && v.status !== 'Rescheduled').length;
  const nowWords = t('today184.now', { time: clockWithPeriod(nowMinutes, lang) });

  const nowLine = (
    <li key="now" className="t184-now" data-testid="t184-now">
      <span>{nowWords}</span>
    </li>
  );

  const list = (
    <ol className="t184-list" data-testid="t184-list" aria-labelledby="t184-day-head">
      {items.map((i, n) => [
        n === nowAt ? nowLine : null,
        <li key={i.id} data-testid={`t184-item-${i.id}`}>
          <LineButton item={i} onOpen={open} />
        </li>,
      ])}
      {nowAt === items.length ? nowLine : null}
    </ol>
  );

  const whole = items.filter((i) => i.minutes === null);
  const col = (p: HouseProgram) => items.filter((i) => i.minutes !== null && i.category === p);
  const grid = (
    <div className="t184-gridwrap">
      {whole.length ? (
        <ul className="t184-allday" aria-label={t('today184.allDay')}>
          {whole.map((i) => (
            <li key={i.id}>
              <LineButton item={i} onOpen={open} />
            </li>
          ))}
        </ul>
      ) : null}
      {isToday ? (
        <p className="t184-now t184-now-grid" data-testid="t184-now">
          <span>{nowWords}</span>
        </p>
      ) : null}
      <div className="t184-grid" data-testid="t184-grid">
        {HOUSE_DAY_PROGRAMS.map((p) => {
          const lines = col(p);
          return (
            <section key={p} className="t184-col" aria-labelledby={`h-t184-col-${p}`} data-cat={p} data-testid={`t184-col-${p}`}>
              <h3 id={`h-t184-col-${p}`} className="cal182-tag" data-cat={p}>
                <span className="cal182-dot" aria-hidden="true" />
                {programWord(p, t)}
              </h3>
              {lines.length ? (
                <ol>
                  {lines.map((i) => (
                    <li key={i.id}>
                      <LineButton item={i} onOpen={open} />
                    </li>
                  ))}
                </ol>
              ) : (
                <p className="help">{t('today184.colEmpty')}</p>
              )}
            </section>
          );
        })}
      </div>
    </div>
  );

  return (
    <section aria-labelledby="h-house-today" className="page cal182 t184" data-testid="house-today" data-role={me.role ?? ''}>
      <h1 id="h-house-today" tabIndex={-1} className="t1025-title" data-testid="t1025-title">
        {t('today1025.title')}
      </h1>
      <p className="t1025-hello" data-testid="t184-greeting">
        {name ? t('today1025.hello', { hello, name, day: formatDayKey(todayKey, i18n.language) }) : t('today1025.helloNoName', { hello, day: formatDayKey(todayKey, i18n.language) })}
      </p>
      {q.data ? (
        <p className="t1025-summary" data-testid="t1025-summary">
          {[t('today1025.summary', { count: attentionTotal }), q.data.names === 'full' && q.data.dayKey === todayKey ? t('today1025.summaryVisits', { count: todayVisits }) : null].filter(Boolean).join(' · ')}
        </p>
      ) : null}
      {q.isLoading ? (
        <p className="help" role="status">
          {t('today184.loading')}
        </p>
      ) : q.isError && !q.data ? (
        <p className="help" role="alert" data-testid="t184-error">
          {t('today184.error')}
        </p>
      ) : null}
      {unavailable.length ? (
        <p className="help" role="status" data-testid="t184-unavailable">
          {t('today184.unavailable', { list: unavailable.join(', ') })}
        </p>
      ) : null}

      {/* 3 Nov 2026 (D-1103-NEXT): Do next — one list in order; with the Settings switch off, Today is as it was */}
      {!doNextKnown ? null : doNext ? (
        q.data ? <DoNext attention={q.data.attention} role={me.role} todayKey={todayKey} /> : null
      ) : (
        <>
          {q.data ? <AttentionGroups lines={q.data.attention} /> : null}

          {/* 1 Nov 2026 (D-1102-OFFICE-1): what might slip — the office only, Settings switch */}
          <MightSlip role={me.role} />
        </>
      )}

      <section aria-labelledby="t184-day-head" className="t184-section t1025-day" data-testid="t184-day">
        <div className="t184-daywrap">
          <div className="t184-daymain">
            <h2 id="t184-day-head" data-testid="t184-day-head">
              {isToday ? t('today1025.byTime') : t('today1025.byTimeDay', { day: dayWords })}
            </h2>
            {isPast ? <p className="help t184-record">{t('today184.record')}</p> : null}
            <div className="t184-bar">
              <div role="group" aria-label={t('today184.view')} className="t184-views">
                <Button size="sm" variant={view === 'list' ? 'default' : 'secondary'} aria-pressed={view === 'list'} onClick={() => setView('list')} data-testid="t184-view-list">
                  {t('today184.list')}
                </Button>
                <Button size="sm" variant={view === 'grid' ? 'default' : 'secondary'} aria-pressed={view === 'grid'} onClick={() => setView('grid')} data-testid="t184-view-grid">
                  {t('today184.grid')}
                </Button>
              </div>
              <DayPicker day={day} onPick={pick} />
              {!isToday ? (
                <Button size="sm" variant="secondary" onClick={() => pick(todayKey)} data-testid="t184-back">
                  {t('today184.backToToday')}
                </Button>
              ) : null}
            </div>
            {items.length ? view === 'grid' ? grid : list : <p className="help">{isPast ? t('today184.dayEmptyPast') : t('today184.dayEmpty')}</p>}
          </div>
        </div>
      </section>

      <ThisWeek todayKey={todayKey} selected={day} lines={weekLines} onPick={pick} />

      {emails.length ? (
        <section aria-labelledby="h-t184-emails" className="t184-section" data-testid="t184-emails">
          <h2 id="h-t184-emails">{t('today184.emailsTitle')}</h2>
          <ul className="t184-lines">
            {emails.map((e) => {
              const words = emailWords(e, t, lang);
              return (
                <li key={e.communicationId} className="t184-email" data-state={e.state} data-testid={`t184-email-${e.communicationId}`}>
                  <span className="t184-what">{words}</span>
                  <OpenLink href={e.href} program={null} className="btn secondary sm t184-go" aria-label={`${t('today184.email.open')} — ${words}`} data-testid={`t184-email-open-${e.communicationId}`}>
                    {t('today184.email.open')}
                  </OpenLink>
                </li>
              );
            })}
          </ul>
        </section>
      ) : null}

      {reports.length ? (
        <section aria-labelledby="h-t184-reports" className="t184-section" data-testid="t184-reports">
          <h2 id="h-t184-reports">{t('today184.reportsTitle')}</h2>
          <ul className="t184-lines">
            {reports.map((r) => {
              const words = reportWords(r, t, formatDayShort(r.dayKey, i18n.language));
              return (
                <li key={r.id} className="t184-report" data-cat={r.program ?? undefined} data-testid={`t184-report-${r.id}`}>
                  <span className="t184-what">{words}</span>
                  {r.href ? (
                    <OpenLink href={r.href} program={r.openIn} className="btn secondary sm t184-go" aria-label={`${t('today184.report.read')} — ${words}`} data-testid={`t184-report-open-${r.id}`}>
                      {t('today184.report.read')}
                    </OpenLink>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </section>
      ) : null}

      <LineWindow item={opened} dayKey={day} canCall={q.data?.names === 'full'} onClose={() => setOpened(null)} returnFocusTo={opener} />
    </section>
  );
}
